import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { interviewAPI } from '../api/recruitmentApi';
import { Calendar, Video, MapPin, Clock, User, Mail, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

const InterviewsPage = () => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('upcoming'); // 'upcoming' or 'all'
  const [selectedInterview, setSelectedInterview] = useState(null);

  useEffect(() => {
    fetchInterviews();
  }, [viewMode]);

  const fetchInterviews = async () => {
    try {
      setLoading(true);
      const response = await interviewAPI.getInterviews({
        startDate: viewMode === 'upcoming' ? new Date().toISOString().split('T')[0] : undefined,
        endDate: viewMode === 'upcoming' ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : undefined
      });
      setInterviews(response.interviews || []);
    } catch (error) {
      console.error('Failed to fetch interviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
      'no-show': 'bg-orange-100 text-orange-800',
      rescheduled: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    const icons = {
      video: <Video className="w-4 h-4" />,
      phone: <Phone className="w-4 h-4" />,
      'in-person': <MapPin className="w-4 h-4" />
    };
    return icons[type] || <Calendar className="w-4 h-4" />;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const groupInterviewsByDate = () => {
    const grouped = {};
    interviews.forEach(interview => {
      const date = interview.start?.split('T')[0] || interview.scheduledDate;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(interview);
    });
    return grouped;
  };

  const groupedInterviews = groupInterviewsByDate();
  const sortedDates = Object.keys(groupedInterviews).sort();

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Interview Calendar</h1>
            <p className="text-gray-600 mt-1">Manage and track all scheduled interviews</p>
          </div>
          <div className="flex gap-3">
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="upcoming">Upcoming Interviews</option>
              <option value="all">All Interviews</option>
            </select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Total</p>
                <p className="text-2xl font-bold text-blue-900">{interviews.length}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Scheduled</p>
                <p className="text-2xl font-bold text-green-900">
                  {interviews.filter(i => i.status === 'scheduled').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium">Confirmed</p>
                <p className="text-2xl font-bold text-purple-900">
                  {interviews.filter(i => i.status === 'confirmed').length}
                </p>
              </div>
              <Video className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Completed</p>
                <p className="text-2xl font-bold text-gray-900">
                  {interviews.filter(i => i.status === 'completed').length}
                </p>
              </div>
              <User className="w-8 h-8 text-gray-500" />
            </div>
          </div>
        </div>

        {/* Interviews List */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : interviews.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Interviews Scheduled</h3>
            <p className="text-gray-500">
              {viewMode === 'upcoming' 
                ? 'No upcoming interviews found. Schedule interviews from the applications page.'
                : 'No interviews found in the system.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedDates.map(date => (
              <div key={date} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Date Header */}
                <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">{formatDate(date)}</h3>
                </div>

                {/* Interviews for this date */}
                <div className="divide-y divide-gray-200">
                  {groupedInterviews[date].map(interview => (
                    <motion.div
                      key={interview.id}
                      className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedInterview(interview)}
                      whileHover={{ scale: 1.01 }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`p-2 rounded-lg bg-blue-50`}>
                              {getTypeIcon(interview.type)}
                            </div>
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900">{interview.title}</h4>
                              <p className="text-sm text-gray-600">
                                {interview.candidate?.name} - {interview.job?.title}
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Clock className="w-4 h-4" />
                              <span>{formatTime(interview.start?.split('T')[1]?.split(':').slice(0, 2).join(':'))}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <User className="w-4 h-4" />
                              <span>{interview.duration} min</span>
                            </div>
                            {interview.location && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <MapPin className="w-4 h-4" />
                                <span className="truncate">{interview.location}</span>
                              </div>
                            )}
                            {interview.meetingLink && (
                              <div className="flex items-center gap-2 text-sm">
                                <Video className="w-4 h-4 text-blue-600" />
                                <a 
                                  href={interview.meetingLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline truncate"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Join Meeting
                                </a>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="ml-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(interview.status)}`}>
                            {interview.status}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Interview Detail Modal */}
        {selectedInterview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedInterview.title}</h2>
                  <button
                    onClick={() => setSelectedInterview(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Candidate Info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Candidate</h3>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                      {selectedInterview.candidate?.name?.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{selectedInterview.candidate?.name}</p>
                      <p className="text-sm text-gray-600">{selectedInterview.candidate?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Interview Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Date & Time</h3>
                    <p className="text-gray-900">{formatDate(selectedInterview.start?.split('T')[0])}</p>
                    <p className="text-gray-600">{formatTime(selectedInterview.start?.split('T')[1]?.split(':').slice(0, 2).join(':'))}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Duration</h3>
                    <p className="text-gray-900">{selectedInterview.duration} minutes</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Type</h3>
                    <p className="text-gray-900 capitalize">{selectedInterview.type}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Status</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedInterview.status)}`}>
                      {selectedInterview.status}
                    </span>
                  </div>
                </div>

                {/* Location/Link */}
                {selectedInterview.location && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Location</h3>
                    <p className="text-gray-900">{selectedInterview.location}</p>
                  </div>
                )}

                {selectedInterview.meetingLink && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Meeting Link</h3>
                    <a 
                      href={selectedInterview.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-2"
                    >
                      <Video className="w-4 h-4" />
                      Join Video Meeting
                    </a>
                  </div>
                )}

                {/* Position */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Position</h3>
                  <p className="text-gray-900">{selectedInterview.job?.title}</p>
                  <p className="text-sm text-gray-600">{selectedInterview.job?.department}</p>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setSelectedInterview(null)}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default InterviewsPage;
